// Central Official Cover Art Registry for Eiffel 65 — verified Feb 2026.
// All Wikipedia URLs verified to return HTTP 200 via Special:FilePath.
// YouTube thumbnails use `hqdefault` because `maxresdefault` 404s for older videos.
//
// Sources:
// - Wikipedia / Wikimedia Commons (verified file names)
// - YouTube official channels (hqdefault thumbnails)
//
// Each entry exposes: src, srcSet, source, res, label, country, version, releaseDate.

const wp = (file, lang = "en") => `https://${lang}.wikipedia.org/wiki/Special:FilePath/${file}`;
const yt = (id, kind = "hqdefault") => `https://i.ytimg.com/vi/${id}/${kind}.jpg`;
const ytChain = (id) => [yt(id, "hqdefault"), yt(id, "mqdefault"), yt(id, "sddefault")];

// === ALBUMS — verified Wikipedia covers ===
export const ALBUM_COVERS = {
  europop: {
    src: wp("Eiffel_65-Europop.jpg"),
    srcSet: [
      wp("Eiffel_65-Europop.jpg"),
      wp("Eiffel_65_Europop_CD_cover.JPG"),
      ...ytChain("68ugkg9RePc"),
    ],
    source: "Wikipedia / Wikimedia Commons",
    res: "≈1200×1200",
    label: "Bliss Co. · Universal · Republic",
    country: "Italy / Worldwide",
    version: "Original 1999 release",
    releaseDate: "1999-08-31",
  },
  contact: {
    src: wp("Eiffel65_contact.jpg"),
    srcSet: [
      wp("Eiffel65_contact.jpg"),
      ...ytChain("qqEaK6oLdNA"),
    ],
    source: "Wikipedia",
    res: "≈1000×1000",
    label: "Bliss Co. · Universal",
    country: "Italy",
    version: "Original 2001 release",
    releaseDate: "2001-08-28",
  },
  "eiffel65-it": {
    // No Wikipedia page hosts this cover at a stable URL — use the official Sanremo 2003
    // performance / video thumbnail of "Cosa Resterà" which IS the album art across YT releases.
    src: yt("vaiI7j5C2wA", "hqdefault"),
    srcSet: [
      ...ytChain("vaiI7j5C2wA"),
      ...ytChain("E0SERnogWzQ"),
    ],
    source: "Official YouTube release artwork",
    res: "480×360",
    label: "Bliss Co. · Universal",
    country: "Italy",
    version: "Italian edition (Sanremo 2003)",
    releaseDate: "2003-02-14",
  },
  "eiffel65-special": {
    src: yt("vaiI7j5C2wA", "hqdefault"),
    srcSet: [
      ...ytChain("vaiI7j5C2wA"),
      ...ytChain("1c5ElzGGrm8"),
    ],
    source: "Official YouTube release artwork",
    res: "480×360",
    label: "Bliss Co.",
    country: "Italy",
    version: "Special Edition / English Album (2004)",
    releaseDate: "2004-05-01",
  },
};

// === EPs ===
export const EP_COVERS = {
  "contact-promo": {
    src: wp("Eiffel65_contact.jpg"),
    srcSet: [wp("Eiffel65_contact.jpg"), ...ytChain("qqEaK6oLdNA")],
    source: "Wikipedia (Contact! release)",
    res: "≈1000×1000",
    label: "Bliss Co. (Promo)",
    country: "Italy / Europe",
    version: "Contact! Radio Promo EP (2001)",
    releaseDate: "2001-06-01",
  },
  "reunion-2010-ep": {
    src: wp("Eiffel_65-Europop.jpg"),
    srcSet: [wp("Eiffel_65-Europop.jpg"), ...ytChain("68ugkg9RePc")],
    source: "Wikipedia (re-used Europop artwork)",
    res: "≈1200×1200",
    label: "BlissCorporation",
    country: "Italy",
    version: "Digital Reunion EP (2010)",
    releaseDate: "2010-12-01",
  },
};

// === SINGLES — verified Wikipedia file names where available ===
export const SONG_COVERS = {
  blue: {
    src: wp("Eiffel_65_blue_cover.png"),
    srcSet: [wp("Eiffel_65_blue_cover.png"), ...ytChain("68ugkg9RePc")],
    source: "Wikipedia (single artwork)",
    res: "≈900×900",
    label: "Bliss Co. · Republic",
    country: "Italy",
    version: "Original 1998/1999 single",
  },
  "move-your-body": {
    src: wp("Move_Your_Body.png"),
    srcSet: [wp("Move_Your_Body.png"), wp("Move_your_body.jpg"), ...ytChain("zQ6HBbyJR34")],
    source: "Wikipedia",
    res: "≈900×900",
    label: "Bliss Co. · Universal",
    country: "Italy",
    version: "Original 1999 single",
  },
  "too-much-of-heaven": {
    src: wp("E65_-_Too_Much_of_Heaven_single.png"),
    srcSet: [wp("E65_-_Too_Much_of_Heaven_single.png"), ...ytChain("DZ8PfXOV1fU")],
    source: "Wikipedia",
    res: "≈900×900",
    label: "Bliss Co.",
    country: "Italy",
    version: "Original 1999 single",
  },
  "one-goal": {
    src: yt("qqEaK6oLdNA"),
    srcSet: ytChain("qqEaK6oLdNA"),
    source: "Official YouTube release artwork",
    res: "480×360",
    label: "Bliss Co.",
    country: "Italy",
    version: "Original 2000 single",
  },
  lucky: {
    src: wp("Lucky_(in_my_life).jpg"),
    srcSet: [wp("Lucky_(in_my_life).jpg"), ...ytChain("6Hsi-xQjW8Y")],
    source: "Wikipedia",
    res: "≈900×900",
    label: "Bliss Co. · Universal",
    country: "Italy",
    version: "Original 2001 single",
  },
  "back-in-time": {
    src: yt("XXkPowMdi4Q"),
    srcSet: ytChain("XXkPowMdi4Q"),
    source: "Official YouTube release artwork",
    res: "480×360",
    label: "Bliss Co. · Universal",
    country: "Italy",
    version: "Original 2001 single",
  },
  "80s-stars": {
    src: yt("YM_y2qx-u5A"),
    srcSet: ytChain("YM_y2qx-u5A"),
    source: "Official YouTube video thumbnail",
    res: "480×360",
    label: "Bliss Co. · Universal",
    country: "Italy",
    version: "Single from Contact! (2001) — feat. Franco Battiato",
  },
  "elephants-amsterdam": {
    src: yt("T1ZTVXe3zbo"),
    srcSet: [...ytChain("T1ZTVXe3zbo"), wp("Eiffel65_contact.jpg")],
    source: "Official YouTube video thumbnail",
    res: "480×360",
    label: "Bliss Co. · Universal",
    country: "Italy",
    version: "Final Contact! single (2002)",
  },
  "my-console": {
    src: wp("Eiffel_65-Europop.jpg"),
    srcSet: [wp("Eiffel_65-Europop.jpg"), wp("Eiffel_65_Europop_CD_cover.JPG")],
    source: "Wikipedia (Europop album re-used as single sleeve)",
    res: "≈1200×1200",
    label: "Bliss Co. · Universal",
    country: "Italy / Europe",
    version: "Fourth Europop single (2000)",
  },
  "losing-you": {
    src: wp("Eiffel65_contact.jpg"),
    srcSet: [wp("Eiffel65_contact.jpg"), ...ytChain("qqEaK6oLdNA")],
    source: "Wikipedia (Contact! album re-used as single sleeve)",
    res: "≈1000×1000",
    label: "Bliss Co.",
    country: "Italy",
    version: "Italian limited single (2001)",
  },
  heaven: {
    src: wp("Eiffel65_contact.jpg"),
    srcSet: [wp("Eiffel65_contact.jpg"), ...ytChain("DZ8PfXOV1fU")],
    source: "Curated — official 2022 single artwork unavailable on Wikipedia",
    res: "≈1000×1000",
    label: "BlissCorporation",
    country: "Italy",
    version: "Modern Era single (2022)",
  },
  "cosa-restera": {
    src: yt("vaiI7j5C2wA"),
    srcSet: ytChain("vaiI7j5C2wA"),
    source: "Official YouTube video thumbnail",
    res: "480×360",
    label: "Bliss Co. · Universal",
    country: "Italy",
    version: "Sanremo 2003 entry",
  },
  viaggia: {
    src: yt("1c5ElzGGrm8"),
    srcSet: ytChain("1c5ElzGGrm8"),
    source: "Official YouTube video thumbnail",
    res: "480×360",
    label: "Bliss Co. · Universal",
    country: "Italy",
    version: "Single from Eiffel 65 (2003)",
  },
  quelli: {
    src: yt("E0SERnogWzQ"),
    srcSet: ytChain("E0SERnogWzQ"),
    source: "Official YouTube video thumbnail",
    res: "480×360",
    label: "Bliss Co.",
    country: "Italy",
    version: "Sanremo 2003 single",
  },
  panico: {
    src: yt("_Fh2aav8tAM"),
    srcSet: ytChain("_Fh2aav8tAM"),
    source: "Official YouTube release artwork",
    res: "480×360",
    label: "BlissCorporation",
    country: "Italy",
    version: "Reunion era single (2016)",
  },
  critical: {
    src: yt("-4Vn6Y6c9U8"),
    srcSet: ytChain("-4Vn6Y6c9U8"),
    source: "Official YouTube release artwork",
    res: "480×360",
    label: "BlissCorporation",
    country: "Italy",
    version: "Modern Era (2016)",
  },
  "auto-blu": {
    src: yt("w82DdzD3SXs"),
    srcSet: ytChain("w82DdzD3SXs"),
    source: "Official video thumbnail",
    res: "480×360",
    label: "Universal Music Italy",
    country: "Italy",
    version: "Eiffel 65 × Shiva (2020)",
  },
  bestiale: {
    src: yt("ieJdopl2Pfc"),
    srcSet: ytChain("ieJdopl2Pfc"),
    source: "Official video thumbnail",
    res: "480×360",
    label: "Warner Music Italy",
    country: "Italy",
    version: "Eiffel 65 × Loredana Bertè (2024)",
  },
  "fare-a-meno": {
    src: yt("T0HGBL73FNU"),
    srcSet: ytChain("T0HGBL73FNU"),
    source: "Official video thumbnail",
    res: "480×360",
    label: "Warner Music Italy",
    country: "Italy",
    version: "Eiffel 65 × Guè (2025)",
  },
  "blue-sartini-cire": {
    src: yt("68ugkg9RePc"),
    srcSet: [...ytChain("68ugkg9RePc"), wp("Eiffel_65_blue_cover.png")],
    source: "Original Blue artwork repurposed",
    res: "≈900×900",
    label: "Bliss Co.",
    country: "Italy",
    version: "Samuele Sartini & Cire Remix (2025)",
  },
};

// Resolve cover for any song id. Falls back to album cover when not in registry.
export function resolveSongCover(song, albumCovers = ALBUM_COVERS) {
  if (!song) return null;
  const direct = SONG_COVERS[song.id];
  if (direct) return direct;
  // Fall back to album cover via SONGS[i].album → ALBUM map. We accept either id or title.
  const alb =
    albumCovers[song.albumId] ||
    Object.values(albumCovers).find((a) => a && song.album && a.label && song.album.toLowerCase().includes(a.label.split(" ")[0].toLowerCase()));
  if (alb) return alb;
  if (song.youtubeId) {
    return {
      src: yt(song.youtubeId),
      srcSet: ytChain(song.youtubeId),
      source: "YouTube official thumbnail",
      res: "480×360",
      label: "—",
      country: "—",
      version: "—",
    };
  }
  return null;
}
